/**
 * emailHistory — pure undo/redo transitions for the Email Designer store.
 *
 * Kept free of zustand/React so it can be unit-tested directly (node --test).
 * A "snapshot" is the undoable slice of store state: blocks + emailSettings +
 * customVariables. History is two stacks (`_past`, `_future`) plus a coalescing
 * tag so a continuous edit (dragging a slider, typing) collapses into ONE step.
 */

export const HISTORY_LIMIT = 50;   // cap the undo depth (memory bound)
export const COALESCE_MS = 600;    // same-tag edits within this window = one step

// The slice of state we snapshot. References are stable because every store
// mutation already replaces these immutably, so no deep clone is needed.
export function snapshot(state) {
  return {
    blocks: state.blocks,
    emailSettings: state.emailSettings,
    customVariables: state.customVariables,
  };
}

/**
 * Compute the history fields to merge BEFORE applying a mutation.
 * @param state current (pre-mutation) store state
 * @param tag   optional coalescing key (e.g. `update:blk_3`); discrete ops pass none
 * @param now   Date.now() (injected for deterministic tests)
 * @returns a partial { _past, _future, _lastTag, _lastTs } to spread into set()
 */
export function recordHistory(state, tag, now) {
  const past = state._past || [];
  // Coalesce: same continuous edit within the window → don't add a new step.
  if (tag && state._lastTag === tag && now - (state._lastTs || 0) < COALESCE_MS) {
    return { _lastTs: now };
  }
  const next = [...past, snapshot(state)];
  if (next.length > HISTORY_LIMIT) next.shift();
  return { _past: next, _future: [], _lastTag: tag || null, _lastTs: now };
}

// Restore the previous snapshot; push the current one onto the redo stack.
// Returns null when there is nothing to undo.
export function undoState(state) {
  const past = state._past || [];
  if (!past.length) return null;
  const previous = past[past.length - 1];
  return {
    ...previous,
    _past: past.slice(0, -1),
    _future: [snapshot(state), ...(state._future || [])],
    _lastTag: null,
    _lastTs: 0,
    selectedBlockId: null,
    selectedBlockContext: null,
  };
}

// Re-apply the next snapshot; push the current one back onto the undo stack.
// Returns null when there is nothing to redo.
export function redoState(state) {
  const future = state._future || [];
  if (!future.length) return null;
  const next = future[0];
  return {
    ...next,
    _past: [...(state._past || []), snapshot(state)],
    _future: future.slice(1),
    _lastTag: null,
    _lastTs: 0,
    selectedBlockId: null,
    selectedBlockContext: null,
  };
}

// Fresh history (used on template load / clear).
export function emptyHistory() {
  return { _past: [], _future: [], _lastTag: null, _lastTs: 0 };
}
